import Layout from "@/components/Layout";
import CTASection from "@/components/CTASection";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import {
  Shield, Brain, FileCheck, Users, AlertTriangle, BookOpen,
  ClipboardCheck, Eye, ArrowRight, CheckCircle2, UserCheck, Sparkles
} from "lucide-react";
import heroPlatform from "@/assets/privacy360-devices-hero.jpg";
import heroPlatform640 from "@/assets/privacy360-devices-hero-640.jpg";
import heroPlatform960 from "@/assets/privacy360-devices-hero-960.jpg";
import heroPlatform1280 from "@/assets/privacy360-devices-hero-1280.jpg";
import heroPlatformWebp from "@/assets/privacy360-devices-hero.webp";
import heroPlatform640Webp from "@/assets/privacy360-devices-hero-640.webp";
import heroPlatform960Webp from "@/assets/privacy360-devices-hero-960.webp";
import heroPlatform1280Webp from "@/assets/privacy360-devices-hero-1280.webp";
import { ORG_APPLY_URL, ORG_APPLY_HELPER_COPY } from "@/lib/organisationSignup";
import SEO from "@/components/SEO";
import { baseSiteSchema, buildFaqSchema, buildWebPageSchema, organisationSignupOfferSchema } from "@/lib/schema";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const pillars = [
  {
    icon: Shield,
    title: "Privacy Assessments",
    desc: "Run DPIAs, LIAs, transfer, vendor and broader programme assessments with structured rationale, evidence and sign-off.",
    link: "/platform",
  },
  {
    icon: Brain,
    title: "AI Governance",
    desc: "Register AI systems, run assessments, track remediation, manage suppliers and maintain oversight across your AI portfolio.",
    link: "/ai-governance",
  },
  {
    icon: BookOpen,
    title: "Training & Evidence",
    desc: "Deliver role-based privacy and AI governance training with completion evidence, audit trails and reporting built in.",
    link: "/modules",
  },
  {
    icon: UserCheck,
    title: "Consent Management",
    desc: "Capture, validate, and evidence consent across websites, APIs, imports, and legacy datasets in one operational module.",
    link: "/consent-management",
    badge: "Now Live",
  },
  {
    icon: FileCheck,
    title: "Contract Review",
    desc: "Review processor agreements, DPAs and supplier terms with structured workflows, evidence capture and commercial oversight.",
    link: "/modules/contract-review",
    badge: "Controlled Release",
  },
];

const modules = [
  { icon: ClipboardCheck, label: "Privacy Assessments" },
  { icon: FileCheck, label: "Records & Operations" },
  { icon: Users, label: "DSAR Requests" },
  { icon: AlertTriangle, label: "Breach Management" },
  { icon: UserCheck, label: "Consent Management", badge: "Now Live" },
  { icon: FileCheck, label: "Contract Review", badge: "Controlled Release" },
  { icon: Shield, label: "Vendor Assessments" },
  { icon: Eye, label: "AI System Register" },
  { icon: Brain, label: "AI Assessments" },
  { icon: CheckCircle2, label: "Evidence Management" },
];

const trustItems = [
  "Enterprise-grade security",
  "Multi-jurisdiction ready",
  "Audit-ready workflows",
  "Multilingual delivery",
];

const comparisonGuides = [
  {
    title: "Privacy360 vs OneTrust",
    desc: "Compare operational privacy and AI governance workflows with a broader enterprise governance platform.",
    link: "/compare/privacy360-vs-onetrust",
  },
  {
    title: "Privacy360 vs TrustArc",
    desc: "Compare two privacy-led platforms across daily programme work, AI governance, consent and training.",
    link: "/compare/privacy360-vs-trustarc",
  },
  {
    title: "Privacy360 vs PrivIQ",
    desc: "Compare practical GDPR workflows with a wider operational platform for privacy and AI governance.",
    link: "/compare/privacy360-vs-priviq",
  },
  {
    title: "OneTrust vs TrustArc vs Privacy360",
    desc: "Review all three platforms by operating model, scope and the work privacy teams manage each day.",
    link: "/compare/privacy360-vs-onetrust-vs-trustarc",
  },
];

const homeDescription = "Privacy360 by Formiti Data International — the operational platform for AI governance, DPIA, ROPA, DSAR, consent, contracts and audit-ready evidence.";

const homeFaqs = [
  {
    q: "Is Privacy360 a consultancy or a platform?",
    a: "It is a software platform. Privacy360 is built by Formiti Data International, a privacy consultancy, so the workflows reflect how privacy programmes actually run — but what you buy is the operational platform your team works in every day.",
  },
  {
    q: "How long does it take to get started?",
    a: "Accounts are typically live within 72 hours of a completed application. We confirm your plan, entity count and region, issue your contract and provision your account — there is no six-month implementation project.",
  },
  {
    q: "Does it replace our spreadsheets and shared drives?",
    a: "Yes. Records of processing, DSARs, breaches, assessments, consent and evidence move into structured workflows with ownership, deadlines and audit trails — instead of static files that drift out of date.",
  },
  {
    q: "Is AI governance included or an add-on?",
    a: "Included. Every plan ships with the AI System Register, AI assessments, supplier oversight, remediation tracking and AI evidence — there is no separate AI governance add-on to buy.",
  },
  {
    q: "Where is our data hosted?",
    a: "You choose your preferred data region when you apply — EU, USA, India or Asia-Pacific. See our data residency page for how regional hosting works.",
  },
];

const Index = () => (
  <Layout>
    <SEO
      title="Privacy360 by Formiti | AI Governance & Privacy Platform"
      description={homeDescription}
      canonicalPath="/"
      jsonLd={[
        ...baseSiteSchema,
        buildWebPageSchema("/", "Privacy360 privacy and AI governance platform", homeDescription, {
          mainEntity: { "@id": "https://privacy360.io/#software" },
          potentialAction: [
            { "@type": "ScheduleAction", name: "Book Demo", target: "https://privacy360.io/book-demo" },
            { "@type": "RegisterAction", name: "Apply for an account", target: "https://privacy360.io/organisation/apply" },
          ],
        }),
        organisationSignupOfferSchema,
        buildFaqSchema(homeFaqs),
      ]}
    />
    {/* Hero */}
    <section className="gradient-hero py-20 lg:py-28">
      <div className="container">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h1 className="text-4xl lg:text-6xl font-bold leading-tight">
              The Enterprise Command Center for Global Data Privacy & AI Governance
            </h1>
            <p className="mt-4 text-lg text-secondary max-w-3xl">
              Move beyond fragmented spreadsheets. Privacy360 by Formiti operationalizes multi-jurisdictional compliance, vendor risk, and AI systems within a single, audit-ready platform.
            </p>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start mt-8">
              <div className="flex flex-col gap-2">
                <Link to="/book-demo">
                  <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-8">
                    Book Demo
                  </Button>
                </Link>
              </div>
              <div className="flex flex-col gap-2">
                <a href={ORG_APPLY_URL}>
                  <Button size="lg" variant="outline" className="font-semibold px-8">
                    Apply for an account
                  </Button>
                </a>
                
              </div>
              <Link to="/platform">
                <Button size="lg" variant="outline" className="font-semibold px-8">
                  Explore the platform
                </Button>
              </Link>
            </div>
          </div>
          <div className="relative">
            <div className="rounded-xl overflow-hidden shadow-2xl shadow-primary/10 border border-border/40">
              <picture>
                <source
                  type="image/webp"
                  srcSet={`${heroPlatform640Webp} 640w, ${heroPlatform960Webp} 960w, ${heroPlatform1280Webp} 1280w, ${heroPlatformWebp} 1920w`}
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 606px"
                />
                <img
                  src={heroPlatform}
                  srcSet={`${heroPlatform640} 640w, ${heroPlatform960} 960w, ${heroPlatform1280} 1280w, ${heroPlatform} 1920w`}
                  sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 606px"
                  alt="Privacy360 platform shown on desktop, tablet and mobile"
                  width={1920}
                  height={1071}
                  fetchPriority="high"
                  className="w-full h-auto"
                />
              </picture>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Trust strip */}
    <section className="py-6 border-b border-border/60 surface-elevated">
      <div className="container flex flex-wrap justify-center gap-x-8 gap-y-3">
        {trustItems.map((item) => (
          <div key={item} className="flex items-center gap-2 text-sm text-tertiary">
            <CheckCircle2 className="h-4 w-4 text-primary" />
            <span>{item}</span>
          </div>
        ))}
      </div>
    </section>

    <section className="border-b border-border/60 bg-primary/5 py-4">
      <div className="container flex flex-col items-center justify-center gap-3 text-sm sm:flex-row">
        <span className="font-medium text-foreground/80">Recently added capabilities</span>
        <div className="flex flex-wrap justify-center gap-2">
          <Badge variant="secondary" className="border-0 bg-background text-primary shadow-sm">Consent Management · Now Live</Badge>
          <Badge variant="secondary" className="border-0 bg-background text-primary shadow-sm">Contract Review · Controlled Release</Badge>
        </div>
      </div>
    </section>

    {/* Who it's for */}
    <section className="py-20">
      <div className="container text-center mb-14">
        <h2 className="text-3xl md:text-4xl font-semibold mb-4">Built for real privacy teams, not just checklists</h2>
        <p className="text-secondary text-lg max-w-2xl mx-auto">
          Privacy360 fits the way modern privacy, compliance, security and AI teams actually work — across in-house programmes, cross-border legal entities and product-led organisations.
        </p>
      </div>
      <div className="container grid md:grid-cols-3 gap-6">
        {[
          {
            title: "In-house privacy & compliance teams",
            desc: "Run DPIAs, records, DSARs, consent and AI governance in one operational workspace, with shared evidence and reporting.",
            link: "/platform",
            cta: "See the platform",
          },
          {
            title: "Organisations managing cross-border entities",
            desc: "Manage privacy programmes, assessments, evidence, consent and contract review across multiple legal entities and jurisdictions.",
            link: "/platform",
            cta: "See the platform",
          },
          {
            title: "Product, security & AI owners",
            desc: "Govern AI use cases, vendor risk and data flows alongside privacy work — without waiting for a six-month platform rollout.",
            link: "/ai-governance",
            cta: "See AI Governance",
          },
        ].map((p) => (
          <div key={p.title} className="rounded-xl border border-border/60 surface-elevated p-8 flex flex-col">
            <h3 className="text-lg font-semibold mb-3">{p.title}</h3>
            <p className="text-secondary text-sm leading-relaxed mb-5 flex-1">{p.desc}</p>
            <Link to={p.link} className="text-primary text-sm font-medium inline-flex items-center gap-1 hover:gap-2 transition-all">
              {p.cta} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        ))}
      </div>
    </section>

    {/* Flagship modules */}
    <section className="py-20 surface-subtle">
      <div className="container text-center mb-14">
        <h2 className="text-3xl md:text-4xl font-semibold mb-4">The modules that matter most right now</h2>
        <p className="text-secondary text-lg max-w-2xl mx-auto">
          Three flagship modules anchor how teams use Privacy360 day to day — each connected to your records, suppliers and evidence.
        </p>
      </div>
      <div className="container grid md:grid-cols-3 gap-6">
        {[
          {
            icon: UserCheck,
            title: "Consent Management",
            desc: "Multi-jurisdiction consent and preference management that protects privacy, keeps Core Web Vitals healthy and feeds clean signals into your analytics and CRM.",
            link: "/consent-management",
          },
          {
            icon: Brain,
            title: "AI Governance",
            desc: "Keep a live register of AI systems, run risk and impact assessments, track suppliers and maintain audit-ready evidence — all alongside your existing privacy work.",
            link: "/ai-governance",
          },
          {
            icon: FileCheck,
            title: "Contract Review",
            desc: "AI-assisted review of DPAs, SCCs, data sharing agreements and AI vendor terms that pushes structured outcomes into vendor risk, RoPA and AI registers.",
            link: "/modules/contract-review",
          },
        ].map((m) => (
          <Link
            key={m.title}
            to={m.link}
            className="group rounded-xl border border-border/60 surface-elevated p-8 hover:shadow-lg hover:shadow-primary/5 transition-all"
          >
            <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-5">
              <m.icon className="h-6 w-6 text-primary" />
            </div>
            <h3 className="text-xl font-semibold mb-3">{m.title}</h3>
            <p className="text-secondary text-sm leading-relaxed mb-4">{m.desc}</p>
            <span className="text-primary text-sm font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
              Learn more <ArrowRight className="h-4 w-4" />
            </span>
          </Link>
        ))}
      </div>
    </section>

    <section className="py-20">
      <div className="container text-center mb-14">
        <h2 className="text-3xl md:text-4xl font-semibold mb-4">
          Privacy and AI governance, operationalised
        </h2>
        <p className="text-secondary text-lg max-w-2xl mx-auto">
              Move beyond static registers. Privacy360 gives your team structured workflows, operational visibility and audit-ready evidence across privacy, AI governance and trust.
        </p>
      </div>

      <div className="container grid md:grid-cols-2 lg:grid-cols-5 gap-6">
        {pillars.map((p) => (
          <Link
            key={p.title}
            to={p.link}
            className="group rounded-xl border border-border/60 p-8 surface-elevated hover:shadow-lg hover:shadow-primary/5 transition-all"
          >
            <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center mb-5">
              <p.icon className="h-6 w-6 text-primary" />
            </div>
            <div className="mb-3 flex items-center gap-2">
              <h3 className="text-xl font-semibold">{p.title}</h3>
              {p.badge && <Badge variant="secondary" className="border-0 bg-primary/10 text-primary text-[11px]">{p.badge}</Badge>}
            </div>
            <p className="text-secondary text-sm leading-relaxed mb-4">{p.desc}</p>
            <span className="text-primary text-sm font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
              Learn more <ArrowRight className="h-4 w-4" />
            </span>
          </Link>
        ))}
      </div>
    </section>

    {/* AI Governance Spotlight */}
    <section className="py-20 surface-subtle">
      <div className="container grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <span className="inline-block text-xs font-semibold tracking-wider uppercase text-accent mb-3" style={{ color: "hsl(253 100% 65%)" }}>
            AI Governance
          </span>
          <h2 className="text-3xl md:text-4xl font-semibold mb-5">
            A practical operating layer for AI oversight
          </h2>
          <p className="text-secondary leading-relaxed mb-6">
            Register AI systems, run structured assessments, track remediation actions, manage AI suppliers, capture evidence and maintain oversight — all informed by the EU AI Act and international AI regulatory developments.
          </p>
          <ul className="space-y-3 mb-8">
            {["AI System Register", "AI Assessments", "AI Remediation", "AI Suppliers", "AI Evidence", "AI Monitoring"].map((item) => (
              <li key={item} className="flex items-center gap-3 text-sm">
                <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                {item}
              </li>
            ))}
          </ul>
          <Link to="/ai-governance">
            <Button className="bg-primary hover:bg-primary/90 text-primary-foreground">
              Explore AI Governance <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
        <div className="grid grid-cols-2 gap-4">
          {[
            { label: "AI Systems Registered", value: "148" },
            { label: "Assessments Completed", value: "312" },
            { label: "Remediation Actions", value: "89" },
            { label: "Evidence Items", value: "1,204" },
          ].map((stat) => (
            <div key={stat.label} className="rounded-xl border border-border/60 surface-elevated p-6 text-center">
              <div className="text-3xl font-semibold text-primary mb-1">{stat.value}</div>
              <div className="text-xs text-tertiary">{stat.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* AI-Assisted Capabilities */}
    <section className="py-20">
      <div className="container text-center mb-14">
        <h2 className="text-3xl md:text-4xl font-semibold mb-4">
          Embedded AI review, built into the workflow
        </h2>
        <p className="text-secondary text-lg max-w-2xl mx-auto">
          Privacy360 embeds AI-assisted review directly into breach, DPIA, and LIA workflows — giving privacy teams decision support with cited guidance, without replacing human judgement.
        </p>
      </div>

      <div className="container grid md:grid-cols-3 gap-6 mb-8">
        {[
          {
            title: "Breach Guidance",
            desc: "Review incidents faster with embedded AI support that highlights missing facts, notification considerations, and next-step actions.",
            bullets: ["Assesses breach facts and flags gaps", "Highlights notification considerations", "Suggests remediation steps", "Drafts internal assessment wording"],
          },
          {
            title: "DPIA Review",
            desc: "Strengthen DPIAs before sign-off with AI-assisted review that challenges weak reasoning and surfaces missing mitigations.",
            bullets: ["Reviews drafts for gaps and weak reasoning", "Challenges necessity and proportionality", "Identifies missing mitigations", "Supports consistent assessments"],
          },
          {
            title: "LIA Review",
            desc: "Improve legitimate interests assessments with structured AI review of balancing-test logic, risks, and safeguards.",
            bullets: ["Reviews balancing-test reasoning", "Challenges justification gaps", "Identifies areas needing stronger mitigation", "Supports defensible records"],
          },
        ].map((cap) => (
          <div key={cap.title} className="rounded-xl border border-border/60 surface-elevated p-8">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="h-5 w-5 text-primary" />
              <h3 className="text-lg font-semibold">{cap.title}</h3>
            </div>
            <p className="text-secondary text-sm leading-relaxed mb-4">{cap.desc}</p>
            <ul className="space-y-2">
              {cap.bullets.map((b) => (
                <li key={b} className="flex items-start gap-2 text-sm">
                  <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-foreground/85">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>

      <div className="container max-w-2xl">
        <div className="rounded-lg bg-primary/5 border border-primary/20 px-5 py-3 flex items-start gap-3">
          <Shield className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
          <p className="text-sm text-secondary">
            <span className="font-semibold text-foreground">AI-assisted, human-reviewed.</span> AI-generated guidance with human review. Your data is used only to generate this review and is not used to train AI models.
          </p>
        </div>
      </div>
    </section>

    {/* Modules Grid */}
    <section className="py-20 surface-subtle">
      <div className="container text-center mb-14">
        <h2 className="text-3xl md:text-4xl font-semibold mb-4">
          Built around real governance workflows
        </h2>
        <p className="text-secondary text-lg max-w-2xl mx-auto">
            Explore the modules that make up the Privacy360 operational platform for privacy, AI governance, consent, contracts, training and evidence.
        </p>
      </div>
      <div className="container grid grid-cols-2 md:grid-cols-4 gap-4">
        {modules.map((m) => (
          <div
            key={m.label}
            className="rounded-xl border border-border/60 surface-elevated p-6 flex flex-col items-center text-center hover:shadow-md transition-shadow"
          >
            <m.icon className="h-8 w-8 text-primary mb-3" />
            <span className="text-sm font-medium">{m.label}</span>
            {m.badge && <Badge variant="secondary" className="mt-3 border-0 bg-primary/10 text-primary text-[11px]">{m.badge}</Badge>}
          </div>
        ))}
      </div>
      <div className="container text-center mt-10">
        <Link to="/modules">
          <Button variant="outline" size="lg">
            View All Modules <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </Link>
      </div>
    </section>

    {/* Comparison guides */}
    <section className="border-y border-border/60 py-20">
      <div className="container">
        <div className="mb-12 flex flex-col gap-5 md:flex-row md:items-end md:justify-between">
          <div className="max-w-3xl">
            <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-primary">Compare platforms</p>
            <h2 className="mb-4 text-3xl font-semibold md:text-4xl">Choose the right operating model for your privacy team</h2>
            <p className="text-lg leading-relaxed text-secondary">
              Review buyer-oriented comparisons covering platform scope, daily workflows, AI governance and implementation fit.
            </p>
          </div>
          <Button asChild variant="outline" className="shrink-0">
            <Link to="/compare">View all comparisons <ArrowRight className="ml-2 h-4 w-4" /></Link>
          </Button>
        </div>
        <div className="grid gap-5 md:grid-cols-2">
          {comparisonGuides.map((guide) => (
            <Link
              key={guide.link}
              to={guide.link}
              className="group rounded-lg border border-border/60 bg-card p-6 transition-colors hover:border-primary"
            >
              <div className="mb-3 flex items-start justify-between gap-4">
                <h3 className="text-lg font-semibold">{guide.title}</h3>
                <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-tertiary transition-colors group-hover:text-primary" />
              </div>
              <p className="text-sm leading-relaxed text-secondary">{guide.desc}</p>
            </Link>
          ))}
        </div>
      </div>
    </section>

    {/* Jurisdiction spotlight */}
    <section className="py-20 surface-subtle">
      <div className="container">
        <div className="mb-12 max-w-3xl">
          <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-primary">Compliance by jurisdiction</p>
          <h2 className="mb-4 text-3xl font-semibold md:text-4xl">Regional privacy law, covered in depth</h2>
          <p className="text-lg leading-relaxed text-secondary">
            Explore how Privacy360 supports privacy obligations in specific jurisdictions — from where your data is hosted to how India's DPDP Act is managed day to day.
          </p>
        </div>
        <div className="grid gap-5 md:grid-cols-2">
          <Link
            to="/data-residency"
            className="group rounded-xl border border-border/60 bg-card p-8 transition-colors hover:border-primary"
          >
            <h3 className="text-lg font-semibold mb-2">Data residency</h3>
            <p className="text-sm leading-relaxed text-secondary mb-5">
              Choose your preferred data region when you apply — EU, USA, India or Asia-Pacific — with the regional deployment model agreed during enterprise scoping and onboarding.
            </p>
            <span className="text-primary text-sm font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
              Explore data residency <ArrowRight className="h-4 w-4" />
            </span>
          </Link>
          <Link
            to="/india-dpdp-compliance"
            className="group rounded-xl border border-border/60 bg-card p-8 transition-colors hover:border-primary"
          >
            <h3 className="text-lg font-semibold mb-2">India DPDP Act compliance</h3>
            <p className="text-sm leading-relaxed text-secondary mb-5">
              Run DPDP compliance on Privacy360 — consent and notice records, breach duties, grievance redressal, Significant Data Fiduciary obligations and audit-ready evidence.
            </p>
            <span className="text-primary text-sm font-medium inline-flex items-center gap-1 group-hover:gap-2 transition-all">
              See how Privacy360 supports the DPDP Act <ArrowRight className="h-4 w-4" />
            </span>
          </Link>
        </div>
      </div>
    </section>

    {/* FAQ */}
    <section className="py-20">
      <div className="container max-w-3xl">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-semibold mb-4">Common questions</h2>
          <p className="text-secondary text-lg">
            Straight answers to the questions privacy and governance teams ask before they move to Privacy360.
          </p>
        </div>
        <Accordion type="single" collapsible className="w-full">
          {homeFaqs.map((faq, i) => (
            <AccordionItem key={faq.q} value={`faq-${i}`}>
              <AccordionTrigger className="text-left">{faq.q}</AccordionTrigger>
              <AccordionContent className="text-secondary leading-relaxed">
                {faq.a}
                {faq.q.startsWith("Where is our data") && (
                  <>
                    {" "}
                    <Link to="/data-residency" className="font-medium text-primary underline underline-offset-4">
                      Read about data residency
                    </Link>
                    .
                  </>
                )}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>

    {/* CTA */}
    <CTASection />
  </Layout>
);

export default Index;
